import React from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { useTranslation } from 'react-i18next'
import { MapContainer, TileLayer, ImageOverlay } from 'react-leaflet'

import 'leaflet.sync'

import Box from '@material-ui/core/Box'
import CircularProgress from '@material-ui/core/CircularProgress'
import { usePaletteScript, withPaletteScript } from '../../../../adapters/paletteRange'
import { usePaletteAdjustmentsEnabled } from '../../../../context/PaletteAdjustmentsContext'
import { getParcelBounds } from '../../../../utils/map'

import ZoneLayer from './ZoneLayer'
import CloudLegend from '../comparator/CloudLegend'
import LeafletLayersControl from '../../LeafletLayersControl'

// Center map on Granada
const position = [37.741934, -3.188515]

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  loading: {
    marginBottom: theme.spacing(20),
    marginTop: theme.spacing(30),
  },
}))

export const TreeMapLayer = ({
  parcel,
  imagesDate,
  layer,
  setZonedGeom,
  startVegetation,
  zones,
  setTotalPlants,
  selectedResolution,
  data,
  refetch,
  statisticUnit,
  setFeatureData,
  getCustomLayerScript,
  isLoadingFIS,
  indexFilter = null,
}) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const groupRef = React.useRef()

  const [map, setMap] = React.useState(null)

  // Index image with the user's saved palette adjustment for this farm/layer (only in apps
  // that enable it). The palette script also applies the index filter; without an
  // adjustment the plain filter script is used as before.
  const paletteEnabled = usePaletteAdjustmentsEnabled()
  const { data: palette, isLoading: paletteLoading } = usePaletteScript(
    { parcelId: parcel?.id, layerKey: layer?.key, date: startVegetation?.date, indexFilter },
    { enabled: paletteEnabled }
  )
  const indexImageBaseUrl = `${process.env.REACT_APP_BASE_URL_IMAGES}${selectedResolution.instance_id}?${parcel.properties.image_url}&LAYERS=${layer.name}&TIME=${startVegetation?.date}`
  const indexImageUrl = palette?.evalscript_url
    ? withPaletteScript(indexImageBaseUrl, palette)
    : `${indexImageBaseUrl}${getCustomLayerScript()}`
  const [activeBaseLayer, setActiveBaseLayer] = React.useState('Maps Hybrid')
  const [overlays, setOverlays] = React.useState({
    Plants: true,
    'Index layer': true,
  })

  React.useEffect(() => {
    if (imagesDate.length > 0) refetch()
  }, [imagesDate, zones])

  React.useEffect(() => {
    if (data) {
      setZonedGeom({ type: 'seed', value: data })
      setTotalPlants(data.features.length)
    }
  }, [data])

  React.useEffect(() => {
    if (map) {
      try {
        map.fitBounds(getParcelBounds(parcel))
      } catch (error) {
        console.error(error)
      }
    }
  }, [map])

  React.useEffect(() => {
    if (groupRef.current) {
      groupRef.current.clearLayers()
      groupRef.current.addData(data)
    }
  }, [data, statisticUnit])

  const baseLayers = [{ name: 'Maps Hybrid', checked: activeBaseLayer === 'Maps Hybrid' }]
  const overlayGroups = [
    {
      name: 'Tree analysis',
      overlays: [
        { name: 'Plants', visible: overlays['Plants'] },
        { name: 'Index layer', visible: overlays['Index layer'] },
      ],
    },
  ]

  const handleOverlayToggle = (name, visible) =>
    setOverlays((prev) => ({ ...prev, [name]: visible }))

  return (
    <>
      {data && !isLoadingFIS ? (
        <MapContainer
          center={position}
          minZoom={2}
          maxZoom={20}
          zoom={9}
          style={{ width: '100%', height: 500, borderRadius: 4 }}
          zoomControl={false}
          whenCreated={setMap}
        >
          <TileLayer
            maxZoom={20}
            attribution="Maps Hybrid"
            url={`https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}`}
          />

          {overlays['Plants'] && (
            <ZoneLayer
              data={data}
              statisticUnit={statisticUnit}
              color="#03FF03"
              name="Plants"
              ref={groupRef}
              setFeatureData={setFeatureData}
            />
          )}

          {overlays['Index layer'] && !paletteLoading && (
            <ImageOverlay
              key={indexImageUrl}
              bounds={getParcelBounds(parcel)}
              url={indexImageUrl}
              transparent
              interactive
            />
          )}

          <LeafletLayersControl
            baseLayers={baseLayers}
            overlayGroups={overlayGroups}
            onBaseLayerChange={setActiveBaseLayer}
            onOverlayToggle={handleOverlayToggle}
            top={12}
            right={12}
          />

          <CloudLegend cloud_coverage={startVegetation?.cloud_coverage} />
        </MapContainer>
      ) : (
        <Box display="flex" justifyContent="center">
          <CircularProgress className={classes.loading} />
        </Box>
      )}
    </>
  )
}
